import styled from '@emotion/styled';
import { rgba } from 'polished';
import React, { forwardRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import { typography } from '../../../styles/typography';
import { shouldForwardProp } from '../../../utils';
import type { InputVariant } from '../Input';
import { ErrorLabel } from '../Label';

const RadiogroupHost = styled.div`
  width: 100%;
  position: relative;
`;

interface IProps {
  children: React.ReactNode;
  errorMessage?: string;
}

export function Radiogroup({ children, errorMessage }: IProps) {
  return (
    <RadiogroupHost role="radiogroup">
      {children}
      {errorMessage && <ErrorLabel>{errorMessage}</ErrorLabel>}
    </RadiogroupHost>
  );
}

const Radio = styled.input`
  opacity: 0;
  position: absolute;
  transform: translateX(-50000px);

  &:focus + label {
    outline: rgba(77, 97, 171, 0.5) auto 3px;
  }
`;

const Circle = styled(animated.span, {
  shouldForwardProp: shouldForwardProp('span'),
})<{
  currentTheme: string;
  checked: boolean;
}>`
  border: 1px solid
    ${({ theme, checked, currentTheme }) =>
      checked ? 'transparent' : rgba(theme.colors[currentTheme] ?? '', 0.3)};
  height: 24px;
  width: 24px;
  left: 0px;
  top: calc(50% - 12px);
  position: absolute;
  border-radius: 100px;
  background: ${({ theme }) => theme.colors.background};
`;

const AnimatedCircle = styled(animated.span, {
  shouldForwardProp: shouldForwardProp('span'),
})<{
  currentTheme: string;
  checked: boolean;
}>`
  border-color: ${({ theme, checked, currentTheme }) =>
    checked ? theme.colors[currentTheme] : 'transparent'};
  height: 22px;
  border-style: solid;
  width: 22px;
  left: 0px;
  top: calc(50% - 11px);
  position: absolute;
  border-radius: 100px;
`;

const Label = styled.label<{
  currentTheme: string;
  checked: boolean;
  isValid?: boolean;
}>`
  cursor: pointer;
  ${typography.numbers2};
  color: ${({ theme }) => theme.colors.foreground};
  position: relative;
  padding-left: 39px;
  padding-top: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  &:hover > span > span {
    background: ${({ theme, checked, currentTheme }) =>
      checked ? 'transparent' : theme.colors[currentTheme]};
    border: 8px solid
      ${({ theme, checked, currentTheme }) =>
        !checked ? theme.colors.background : theme.colors[currentTheme]};
  }
  & > span {
    border-color: ${({ theme, isValid, currentTheme }) =>
      isValid === false
        ? theme.colors.warning
        : rgba(theme.colors[currentTheme] ?? '', 0.3)};
    ${({ isValid, theme }) =>
      isValid === false &&
      `background-color: ${rgba(theme.colors.warning, 0.1)}`};
  }
  &:hover > span {
    border-color: ${({ theme, currentTheme }) => theme.colors[currentTheme]};
  }
`;

const RadioHost = styled.div<{ variant?: InputVariant; isValid?: boolean }>`
  height: 54px;
  padding-left: 15px;
  box-shadow: 0 0 0 1px
    ${({ variant, theme }) =>
      variant === 'fullBorder' || variant === 'bottomBorder'
        ? rgba(theme?.colors.foreground ?? '', 0.8)
        : 'transparent'};
  background-color: ${({ theme }) => theme.colors.background};
  ${({ theme, isValid }) =>
    isValid !== undefined &&
    isValid === false &&
    `box-shadow: 0 0 0 2px ${theme?.colors.warning}`};
  &:hover {
    ${({ variant, theme }) =>
      variant === 'fullBorder' &&
      `box-shadow: 0 0 0 1px ${rgba(theme?.colors.foreground ?? '', 0.5)}`};
    & > span {
      border-color: ${(props) => props.theme?.colors.foreground};
    }
  }

  &:active {
    box-shadow: 0 0 0 2px ${(props) => props.theme?.shades.primary3};
  }
`;

export interface IRadioProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  theme?: 'primary' | 'secondary' | 'ternary';
  className?: string;
  variant?: InputVariant;
  isValid?: boolean;
}

export const RadioButton = forwardRef<HTMLInputElement, IRadioProps>(
  function RButton(
    {
      label,
      checked,
      onChange,
      theme = 'primary',
      className,
      variant = 'fullBorder',
      isValid,
    },
    ref,
  ) {
    const [focused, setFocused] = useState(false);
    const animatingCircle = useSpring({
      borderWidth: checked || focused ? '8px' : '0px',
      config: { duration: 150 },
    });
    return (
      <RadioHost variant={variant} isValid={isValid}>
        <Label
          currentTheme={theme}
          checked={checked}
          className={className}
          htmlFor={`radio_${label}_${theme}`}
          aria-checked={checked}
          isValid={isValid}
          onMouseEnter={() => {
            setFocused(true);
          }}
          onMouseLeave={() => {
            setFocused(false);
          }}
        >
          <Circle currentTheme={theme} checked={checked}>
            <AnimatedCircle
              currentTheme={theme}
              checked={checked}
              style={{ ...animatingCircle }}
            />
          </Circle>

          {label}
        </Label>
        <Radio
          id={`radio_${label}_${theme}`}
          type="radio"
          checked={checked}
          onChange={(e) => {
            onChange(e.currentTarget.checked);
          }}
          ref={ref}
        />
      </RadioHost>
    );
  },
);
